<template>
  <a :href="computedUrl" ref="vacancyRef" :class="listitemstyle">
    <Paragraph
      :text="props.name"
      :isUmbraco="false"
      :theme="themeType"
      classes="col-span-5 md:w-1/2 md:my-7 text-m font-bold group-hover:text-orange transition-colors pr-2 md:pr-8 break-words hyphens-auto md:hyphens-none md:break-normal md:order-none order-first"
    >
    </Paragraph>
    <div class="flex flex-col md:w-1/5 col-span-3 pr-2 md:pr-0">
      <Paragraph
        :text="props.locationLabel"
        :isUmbraco="false"
        :theme="themeType"
        classes="md:hidden mb-0 font-bold"
      ></Paragraph>

      <Paragraph
        :text="formattedCountries"
        :isUmbraco="false"
        :theme="themeType"
        classes="md:mt-7 md:mb-2 break-words pr-2 md:pr-8 mb-2"
      >
      </Paragraph>
      <Paragraph
        :text="formattedLocations"
        :isUmbraco="false"
        :theme="themeType"
        classes="md:mb-7 break-words pr-2 md:pr-8 mb-0 whitespace-pre-line"
      >
      </Paragraph>
    </div>
    <div class="flex flex-col md:w-1/5 col-span-3 mb-0">
      <Paragraph
        :text="props.applicationDeadlineLabel"
        :isUmbraco="false"
        :theme="themeType"
        classes="md:hidden mb-0 font-bold"
      ></Paragraph>
      <Paragraph
        v-if="props.type != 'noDeadline'"
        :text="showDate(props.closingDate)"
        :isUmbraco="false"
        classes="md:my-7"
        :theme="themeType"
      >
      </Paragraph>
    </div>
    <div class="self-center grow flex justify-end order-first md:order-none mb-6 md:mb-0">
      <Arrow width="24" height="24" :classes="svgClasses" color="orange" />
    </div>
  </a>
</template>

<script setup lang="ts">
import {
  defineProps,
  computed,
  toRefs,
  onMounted,
  onUnmounted,
  ref,
  Ref,
  watch,
} from "vue";
import Paragraph from "@/components/Typography/Paragraph.ce.vue";
import { Theme } from "@/types/types";
import { changeTheme, getThemeType, formatDate } from "@/util/helper";
import { onIntersect } from "@/composables/onIntersect";
import Arrow from "@/components/Atoms/IconLink/Arrow.ce.vue";

export interface IconLinkProps {
  locationLabel?: string;
  applicationDeadlineLabel?: string;
  name: string;
  country?: string;
  closingDate: string;
  refId?: string;
  theme: Theme;
  index: number;
  pageUrl: string;
  type: string;
  locations?: string;
}
const observer: Ref<IntersectionObserver | null> = ref(null);
const vacancyRef = ref(null);

const props = withDefaults(defineProps<IconLinkProps>(), {
  theme: "sand",
});
const { name, country, closingDate, refId, theme, pageUrl, locations } = toRefs(props);

const onEnter = () => {
  changeTheme(theme.value);
};
const onExit = () => {};

onMounted(() => {
  observer.value = onIntersect(vacancyRef.value, onEnter, onExit, false, {
    threshold: 0.6,
  });
});

onUnmounted(() => {
  if (!observer.value) return;
  observer.value.disconnect();
});

watch(theme, (value) => {
  if (!value) return;
  changeTheme(value);
});

const themeType = computed(() => {
  return getThemeType(props.theme);
});

const computedUrl = computed(() => {
  return `${pageUrl.value}?id=${refId?.value}`;
});

const formattedCountries = computed(() => {
  return (country?.value ?? "").split(",").join(", ");
});

const listitemstyle = computed(() => {
  let borderColor = themeType.value === "dark" ? "border-orange" : "border-aubergine";
  let topBorder = props.index === 0 ? "border-t-4" : null;
  return `border-b-4 ${borderColor} ${topBorder} group grid grid-cols-6 md:w-full md:flex py-7 md:py-0 md:items-center`;
});

const formattedLocations = computed(() => {
  return (locations?.value ?? "").split(";").join(", ");
});
const svgClasses = computed(() => {
  let text = themeType.value === "dark" ? "text-white" : "text-aubergine";
  return `stroke-current ${text} group-hover:stroke-orange transition-colors`;
});

const showDate = (datestring: string) => {
  let date = new Date(datestring);
  return formatDate(date, "long");
};
</script>
